import type { SupabaseClient } from '@supabase/supabase-js';

// ============================================================
// Types
// ============================================================

export interface SocialLinks {
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
}

/**
 * Field keys that can be locked by marketing on a sub-brand.
 * App editors should call `isFieldLocked(brand, field)` to disable
 * inputs and show a "Managed by marketing" hint.
 */
export type BrandFieldKey =
  | 'logo_url'
  | 'primary_color'
  | 'secondary_color'
  | 'agency_name'
  | 'font_family'
  | 'font_url'
  | 'cta_label'
  | 'cta_url'
  | 'social_links';

/**
 * A sub-brand kit: one of potentially many brand kits owned by a team.
 * Replaces the old single-row TeamBranding model.
 */
export interface SubBrand {
  id: string;
  team_id: string;
  name: string;
  slug: string | null;
  is_default: boolean;
  locked_fields: BrandFieldKey[];
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  agency_name: string | null;
  font_family: string;
  font_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  social_links: SocialLinks;
  // Only present on listMySubBrands() results.
  can_edit?: boolean;
  member_role?: 'editor' | 'viewer' | null;
  created_at?: string;
  updated_at?: string;
}

export interface TeamBrandingContext {
  team: { id: string; name: string; owner_id: string } | null;
  branding: SubBrand | null;
  role: 'owner' | 'admin' | 'member' | null;
}

// ============================================================
// Helpers
// ============================================================

/**
 * Returns true when the given field is locked on this sub-brand and
 * therefore cannot be overridden per-asset by a non-admin user.
 */
export function isFieldLocked(
  brand: Pick<SubBrand, 'locked_fields'> | null | undefined,
  field: BrandFieldKey
): boolean {
  if (!brand?.locked_fields) return false;
  return brand.locked_fields.includes(field);
}

// ============================================================
// Reads
// ============================================================

/**
 * Get the team-branding context for the currently authenticated user.
 * Used by the Hub to render the branding page header.
 */
export async function getMyTeamBranding(
  supabase: SupabaseClient
): Promise<TeamBrandingContext> {
  const empty: TeamBrandingContext = { team: null, branding: null, role: null };
  try {
    const { data, error } = await supabase.rpc('get_my_team_branding');
    if (error || !data) return empty;
    return data as TeamBrandingContext;
  } catch {
    return empty;
  }
}

/**
 * List every sub-brand the caller can use, with a `can_edit` flag.
 * Powers the Hub list view, the workspace switcher, and per-asset pickers.
 */
export async function listMySubBrands(
  supabase: SupabaseClient
): Promise<SubBrand[]> {
  try {
    const { data, error } = await supabase.rpc('get_my_sub_brands');
    if (error || !data) return [];
    return (data as SubBrand[]).map(normalizeSubBrand);
  } catch {
    return [];
  }
}

/**
 * Resolve the active sub-brand kit for a user. Falls back to that user's
 * team default if no active selection. Used by apps at render time.
 */
export async function getActiveSubBrand(
  supabase: SupabaseClient,
  userId: string
): Promise<SubBrand | null> {
  try {
    const { data, error } = await supabase.rpc('get_active_sub_brand_for_user', {
      p_user_id: userId,
    });
    if (error || !data || !(data as SubBrand).id) return null;
    return normalizeSubBrand(data as SubBrand);
  } catch {
    return null;
  }
}

/**
 * Fetch a single sub-brand by id (used by public viewers when an
 * asset stores `sub_brand_id` for per-asset override).
 */
export async function getSubBrand(
  supabase: SupabaseClient,
  subBrandId: string
): Promise<SubBrand | null> {
  try {
    const { data, error } = await supabase.rpc('get_sub_brand', {
      p_sub_brand_id: subBrandId,
    });
    if (error || !data || !(data as SubBrand).id) return null;
    return normalizeSubBrand(data as SubBrand);
  } catch {
    return null;
  }
}

// ============================================================
// Writes
// ============================================================

/**
 * Set the caller's active sub-brand (workspace switcher).
 * Pass null to clear and fall back to the team default.
 */
export async function setMyActiveSubBrand(
  supabase: SupabaseClient,
  subBrandId: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.rpc('set_my_active_sub_brand', {
      p_sub_brand_id: subBrandId,
    });
    if (error) return { success: false, error: error.message };
    const payload = data as { success: boolean; error?: string };
    return payload?.success
      ? { success: true }
      : { success: false, error: payload?.error || 'Unknown error' };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Unknown error' };
  }
}

export interface SubBrandWritable {
  name?: string;
  slug?: string | null;
  is_default?: boolean;
  locked_fields?: BrandFieldKey[];
  logo_url?: string | null;
  primary_color?: string;
  secondary_color?: string;
  agency_name?: string | null;
  font_family?: string;
  font_url?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  social_links?: SocialLinks;
}

/**
 * Create a new sub-brand for a team. Returns the created row.
 * If `is_default` is true, the previous default is automatically demoted
 * (a partial unique index would otherwise raise a conflict).
 */
export async function createSubBrand(
  supabase: SupabaseClient,
  teamId: string,
  branding: SubBrandWritable & { name: string }
): Promise<{ success: boolean; error?: string; data?: SubBrand }> {
  try {
    if (branding.is_default) {
      await supabase
        .from('team_sub_brands')
        .update({ is_default: false })
        .eq('team_id', teamId)
        .eq('is_default', true);
    }

    const { data, error } = await supabase
      .from('team_sub_brands')
      .insert({ team_id: teamId, ...branding })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: normalizeSubBrand(data as SubBrand) };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Unknown error' };
  }
}

/**
 * Update an existing sub-brand. If `is_default` flips to true, the
 * previous default for that team is demoted first.
 */
export async function saveSubBrand(
  supabase: SupabaseClient,
  subBrandId: string,
  patch: SubBrandWritable
): Promise<{ success: boolean; error?: string; data?: SubBrand }> {
  try {
    if (patch.is_default) {
      const { data: existing } = await supabase
        .from('team_sub_brands')
        .select('team_id')
        .eq('id', subBrandId)
        .single();
      if (existing?.team_id) {
        await supabase
          .from('team_sub_brands')
          .update({ is_default: false })
          .eq('team_id', existing.team_id)
          .eq('is_default', true)
          .neq('id', subBrandId);
      }
    }

    const { data, error } = await supabase
      .from('team_sub_brands')
      .update(patch)
      .eq('id', subBrandId)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: normalizeSubBrand(data as SubBrand) };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Unknown error' };
  }
}

/**
 * Soft-archive a sub-brand. Archived rows are excluded from listings
 * and from RPC fallbacks but are kept for audit / undo.
 */
export async function archiveSubBrand(
  supabase: SupabaseClient,
  subBrandId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('team_sub_brands')
      .update({ archived_at: new Date().toISOString(), is_default: false })
      .eq('id', subBrandId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Unknown error' };
  }
}

/**
 * Upload a logo to the team-branding bucket scoped to a sub-brand.
 * Returns the public URL on success.
 */
export async function uploadSubBrandLogo(
  supabase: SupabaseClient,
  teamId: string,
  subBrandId: string,
  file: File
): Promise<{ url: string | null; error?: string }> {
  const ext = file.name.split('.').pop() || 'png';
  const path = `${teamId}/${subBrandId}/logo-${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('team-branding')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadError) return { url: null, error: uploadError.message };

  const { data } = supabase.storage.from('team-branding').getPublicUrl(path);
  return { url: data.publicUrl };
}

// ============================================================
// Sub-brand membership (per-user grants)
// ============================================================

export interface SubBrandMember {
  id: string;
  sub_brand_id: string;
  user_id: string;
  role: 'editor' | 'viewer';
  created_at: string;
}

export async function listSubBrandMembers(
  supabase: SupabaseClient,
  subBrandId: string
): Promise<SubBrandMember[]> {
  const { data, error } = await supabase
    .from('sub_brand_members')
    .select('*')
    .eq('sub_brand_id', subBrandId);
  if (error || !data) return [];
  return data as SubBrandMember[];
}

export async function addSubBrandMember(
  supabase: SupabaseClient,
  subBrandId: string,
  userId: string,
  role: 'editor' | 'viewer' = 'editor'
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('sub_brand_members')
    .upsert(
      { sub_brand_id: subBrandId, user_id: userId, role },
      { onConflict: 'sub_brand_id,user_id' }
    );
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function removeSubBrandMember(
  supabase: SupabaseClient,
  subBrandId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('sub_brand_members')
    .delete()
    .eq('sub_brand_id', subBrandId)
    .eq('user_id', userId);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

// ============================================================
// Internal
// ============================================================

function normalizeSubBrand(raw: SubBrand): SubBrand {
  return {
    ...raw,
    locked_fields: Array.isArray(raw.locked_fields) ? raw.locked_fields : [],
    social_links: raw.social_links || {},
    primary_color: raw.primary_color || '#0d9488',
    secondary_color: raw.secondary_color || '#06b6d4',
    font_family: raw.font_family || 'Inter',
  };
}
